import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

class ParallaxScaler {
  static defaults = {
    scale: 1.3,
    speed: 0.5,
    ease: 'none',
    scrub: 1,
    markers: false,
    onUpdate: null
  }

  static instances = new Map();
  static instanceCounter = 0;

  constructor(selector, options = {}) {
    if (typeof gsap === 'undefined') {
      throw new Error('ParallaxScaler requires GSAP.');
    }

    if (typeof ScrollTrigger === 'undefined') {
      throw new Error('ParallaxScaler requires ScrollTrigger.');
    }

    gsap.registerPlugin(ScrollTrigger);
    this.elements = this._parseSelector(selector);

    if (this.elements.length === 0) {
      return;
    }

    this.options = { ...ParallaxScaler.defaults, ...options };
    this.elementInstances = [];

    this.elements.forEach(element => {
      const instance = this._createInstance(element);
      this.elementInstances.push(instance);
    });

    this.id = ++ParallaxScaler.instanceCounter;
    ParallaxScaler.instances.set(this.id, this);

    if (this.elements.length === 1) {
      console.log(`ParallaxScaler: Instance #${this.id} created`);
    } else {
      console.log(`ParallaxScaler: Instance #${this.id} created with ${this.elements.length} elements`);
    }
  }

  _parseSelector(selector) {
    let elements = [];

    if (typeof selector === 'string') {
      elements = Array.from(document.querySelectorAll(selector));
    } else if (selector instanceof HTMLElement) {
      elements = [selector];
    } else if (selector instanceof NodeList) {
      elements = Array.from(selector);
    } else if (Array.isArray(selector)) {
      elements = selector.filter(el => el instanceof HTMLElement);
    } else {
      throw new Error('ParallaxScaler: Invalid selector. Use a string CSS, HTMLElement, NodeList or Array.');
    }

    return elements;
  }

  _createInstance(element) {
    if (element.tagName !== 'IMG') {
      console.warn('ParallaxScaler: It is recommended to use <img> elements for better performance', element);
    }

    const container = element.parentElement;

    if (!container) {
      throw new Error('ParallaxScaler: The element does not have a parent container');
    }

    const containerStyle = getComputedStyle(container);
    if (containerStyle.overflow !== 'hidden') {
      console.warn('ParallaxScaler: Applying overflow:hidden to the container. Add this property to your CSS for better performance.');
      container.style.overflow = 'hidden';
    }

    gsap.set(element, {
      scale: this.options.scale,
      transformOrigin: 'center center'
    });

    const scaleExtra = this.options.scale - 1;
    const moveRange = (scaleExtra / 2) * 100;
    const finalRange = moveRange * this.options.speed;

    const animation = gsap.fromTo(
      element,
      {
        yPercent: finalRange
      },
      {
        yPercent: -finalRange,
        ease: this.options.ease,
        scrollTrigger: {
          trigger: container,
          start: 'top bottom',
          end: 'bottom top',
          scrub: this.options.scrub,
          markers: this.options.markers,
          onUpdate: (self) => {
            if (this.options.onUpdate) {
              this.options.onUpdate(self.progress);
            }
          }
        }
      }
    );

    return {
      element,
      container,
      animation,
      scrollTrigger: animation.scrollTrigger
    };
  }

  update(newOptions = {}) {
    this.options = { ...this.options, ...newOptions };
    this.destroy();

    this.elementInstances = [];
    this.elements.forEach(element => {
      const instance = this._createInstance(element);
      this.elementInstances.push(instance);
    });

    console.log(`ParallaxScaler: Instance #${this.id} updated`);
  }

  refresh() {
    this.elementInstances.forEach(instance => {
      if (instance.scrollTrigger) {
        instance.scrollTrigger.refresh();
      }
    });
    console.log(`ParallaxScaler: Instance #${this.id} refreshed`);
  }

  destroy() {
    this.elementInstances.forEach(instance => {
      if (instance.animation) {
        instance.animation.kill();
      }
      if (instance.scrollTrigger) {
        instance.scrollTrigger.kill();
      }
      gsap.set(instance.element, { clearProps: 'all' });
    });

    ParallaxScaler.instances.delete(this.id);
  }

  pause() {
    this.elementInstances.forEach(instance => {
      if (instance.animation) {
        instance.animation.pause();
      }
    });
  }

  resume() {
    this.elementInstances.forEach(instance => {
      if (instance.animation) {
        instance.animation.resume();
      }
    });
  }

  getProgress() {
    if (this.elementInstances[0] && this.elementInstances[0].scrollTrigger) {
      return this.elementInstances[0].scrollTrigger.progress;
    }
    return 0;
  }

  static destroyAll() {
    ParallaxScaler.instances.forEach(instance => instance.destroy());
    ParallaxScaler.instances.clear();
  }

  static refreshAll() {
    ParallaxScaler.instances.forEach(instance => instance.refresh());
  }

  static getInstanceCount() {
    return ParallaxScaler.instances.size;
  }

  static getInstance(id) {
    return ParallaxScaler.instances.get(id);
  }

  static getAllInstances() {
    return Array.from(ParallaxScaler.instances.values());
  }

  static pauseAll() {
    ParallaxScaler.instances.forEach(instance => instance.pause());
  }

  static resumeAll() {
    ParallaxScaler.instances.forEach(instance => instance.resume());
  }

  static autoInit(dataAttribute = 'data-parallax') {
    const elements = document.querySelectorAll(`[${dataAttribute}]`);
    const instances = [];

    elements.forEach(element => {
      try {
        const configStr = element.getAttribute(dataAttribute);
        const config = configStr ? JSON.parse(configStr) : {};
        const instance = new ParallaxScaler(element, config);
        instances.push(instance);
      } catch (error) {
        console.error('ParallaxScaler: Error initializing element', element, error);
      }
    });

    return instances;
  }

  static enableAutoRefresh(debounceTime = 250) {
    let resizeTimer;

    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        ParallaxScaler.refreshAll();
      }, debounceTime);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }
}

export { ParallaxScaler }
